import React, { useState } from 'react'
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Paper, Typography, Chip, IconButton, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@material-ui/core'
import { Undo as UndoIcon } from '@material-ui/icons'

const RateChangeHistory = ({ events, onRevert }) => {
  const [pendingRevert, setPendingRevert] = useState(null)
  const [reverting, setReverting] = useState(false)

  // Find the latest unreverted event per (rateType, roleKey) — only that one is undoable
  const latestByKey = new Map()
  for (const e of events) {
    if (e.reverted) continue
    const key = `${e.rateType}:${e.roleKey}`
    if (!latestByKey.has(key)) latestByKey.set(key, e._id.toString())
  }

  const isUndoable = (e) => {
    if (e.reverted) return false
    const key = `${e.rateType}:${e.roleKey}`
    return latestByKey.get(key) === e._id.toString()
  }

  const confirmRevert = async () => {
    if (!pendingRevert) return
    setReverting(true)
    try {
      await onRevert(pendingRevert)
      setPendingRevert(null)
    } finally {
      setReverting(false)
    }
  }

  return (
    <Paper variant="outlined">
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Who</TableCell>
              <TableCell>When</TableCell>
              <TableCell>Impact</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map(e => {
              const undoable = isUndoable(e)
              return (
                <TableRow key={e._id} hover style={e.reverted ? { opacity: 0.6 } : {}}>
                  <TableCell>
                    <Typography variant="body2">{e.roleName}</Typography>
                    <Typography variant="caption" color="textSecondary">{e.rateType}</Typography>
                  </TableCell>
                  <TableCell>
                    ${e.oldRate ?? '(default)'} → <strong>${e.newRate}</strong>
                    {e.wasFirstOverride && <Chip size="small" label="first override" style={{ marginLeft: 6, fontSize: 10, height: 18 }} variant="outlined" />}
                  </TableCell>
                  <TableCell>{e.actorName || 'unknown'}</TableCell>
                  <TableCell>{new Date(e.changedAt).toLocaleString()}</TableCell>
                  <TableCell>{e.affectedComponentCount} components</TableCell>
                  <TableCell align="right">
                    {e.reverted ? (
                      <Tooltip title={`Reverted by ${e.revertedByName} on ${new Date(e.revertedAt).toLocaleString()}`}>
                        <Chip size="small" label="Reverted" style={{ fontSize: 11, height: 20 }} />
                      </Tooltip>
                    ) : undoable ? (
                      <Tooltip title="Undo this change">
                        <IconButton size="small" onClick={() => setPendingRevert(e)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="A newer change for the same rate is active — undo it first">
                        <span>
                          <IconButton size="small" disabled>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="textSecondary" variant="body2" style={{ padding: 16 }}>
                    No rate changes yet for this bid.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!pendingRevert} onClose={() => setPendingRevert(null)}>
        <DialogTitle>Undo rate change?</DialogTitle>
        <DialogContent>
          {pendingRevert && (
            <Typography>
              Revert <strong>{pendingRevert.roleName}</strong> from ${pendingRevert.newRate} back to ${pendingRevert.oldRate ?? '(default)'}? {pendingRevert.affectedComponentCount} components will be recalculated.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRevert(null)} disabled={reverting}>Cancel</Button>
          <Button onClick={confirmRevert} color="primary" variant="contained" disabled={reverting}>
            {reverting ? <CircularProgress size={16} /> : 'Undo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default RateChangeHistory
